import styled, { css, keyframes } from "styled-components";

import {
  BRAND,
  BRAND_DARK,
  BRAND_LIGHT,
  ERROR,
  ERROR_DARK,
  ERROR_LIGHT,
  ERROR_LIGHTEST,
  GRAY,
  GRAY_LIGHT,
  GRAY_LIGHTEST,
  TEXT_PRIMARY,
  WHITE
} from "../colors";
import actionElements from "../constants/actionElements";
import componentSizes from "../constants/componentSizes";
import buttonThemes from "./buttonActionThemes";
import LoadingIconBase from "./LoadingIcon";

const LOADING_ICON_ID = "loading-icon-id";

export const dimensions = {
  LG_WIDTH: "224px",
  LG_HEIGHT: "46px",
  MD_WIDTH: "160px",
  MD_HEIGHT: "40px",
  SM_WIDTH: "120px",
  SM_HEIGHT: "32px"
};

const DARK_THEMES = [
  buttonThemes.DARK_PRIMARY,
  buttonThemes.DARK_SECONDARY,
  buttonThemes.DARK_TERTIARY
];

export const TERTIARY_THEMES = [
  buttonThemes.TERTIARY,
  buttonThemes.DANGER_TERTIARY,
  buttonThemes.DARK_TERTIARY
];

const buttonThemeStyles = {
  [buttonThemes.DANGER_PRIMARY]: css`
    background-color: ${ERROR};
    border-color: ${ERROR};
    color: ${WHITE};

    &:hover {
      background-color: ${ERROR_LIGHT};
      border-color: ${ERROR_LIGHT};
    }

    &:active {
      background-color: ${ERROR_DARK};
      border-color: ${ERROR_DARK};
    }
  `,
  [buttonThemes.DANGER_SECONDARY]: css`
    background-color: ${ERROR_LIGHTEST};
    border-color: ${ERROR_LIGHT};
    color: ${ERROR};

    &:hover {
      background-color: ${WHITE};
    }

    &:active {
      background-color: ${ERROR_LIGHTEST};
      border-color: ${ERROR_LIGHT};
    }
  `,
  [buttonThemes.DANGER_TERTIARY]: css`
    background: transparent;
    border-color: transparent;
    color: ${ERROR};

    &:hover {
      &:not(svg):not(img) {
        text-decoration: underline;
      }
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.05);
    }
  `,
  [buttonThemes.PRIMARY]: css`
    background-color: ${BRAND};
    border-color: ${BRAND};
    color: ${WHITE};

    &:hover {
      background-color: ${BRAND_LIGHT};
      border-color: ${BRAND_LIGHT};
    }

    &:active {
      background-color: ${BRAND_DARK};
      border-color: ${BRAND_DARK};
    }
  `,
  [buttonThemes.SECONDARY]: css`
    background-color: ${WHITE};
    border-color: ${GRAY};
    color: ${BRAND};

    &:hover {
      border-color: ${GRAY_LIGHT};
    }

    &:active {
      background-color: ${GRAY_LIGHTEST};
      border-color: ${GRAY_LIGHTEST};
    }
  `,
  [buttonThemes.TERTIARY]: css`
    background: transparent;
    border-color: transparent;
    color: ${BRAND};

    &:hover {
      &:not(svg):not(img) {
        text-decoration: underline;
      }
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.05);
    }
  `,
  [buttonThemes.DARK_PRIMARY]: css`
    background: transparent;
    border-color: ${WHITE};
    color: ${WHITE};

    &:hover {
      background-color: ${WHITE};
      border-color: ${WHITE};
      color: ${TEXT_PRIMARY};
    }

    &:active {
      background-color: ${GRAY_LIGHT};
      border-color: ${GRAY_LIGHT};
      color: ${TEXT_PRIMARY};
    }
  `,
  [buttonThemes.DARK_SECONDARY]: css`
    background: transparent;
    border-color: ${WHITE};
    color: ${WHITE};

    &:hover {
      background-color: ${WHITE};
      border-color: ${WHITE};
      color: ${TEXT_PRIMARY};
    }

    &:active {
      background-color: ${GRAY_LIGHT};
      border-color: ${GRAY_LIGHT};
      color: ${TEXT_PRIMARY};
    }
  `,
  [buttonThemes.DARK_TERTIARY]: css`
    background: transparent;
    border-color: transparent;
    color: ${WHITE};

    &:hover {
      &:not(svg):not(img) {
        text-decoration: underline;
      }
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.2);
    }
  `
};

const withThemeStyles = ({ theme }) => buttonThemeStyles[theme];

const buttonSizeStyles = {
  [componentSizes.LG]: css`
    height: ${dimensions.LG_HEIGHT};
    min-width: ${dimensions.LG_WIDTH};
  `,
  [componentSizes.MD]: css`
    height: ${dimensions.MD_HEIGHT};
    min-width: ${dimensions.MD_WIDTH};
  `,
  [componentSizes.SM]: css`
    height: ${dimensions.SM_HEIGHT};
    min-width: ${dimensions.SM_WIDTH};
  `
};

const withSizeStyles = ({ size }) => buttonSizeStyles[size];

const withDisabledStyles = ({ isDisabled, theme }) =>
  isDisabled &&
  css`
    background-color: ${TERTIARY_THEMES.includes(theme)
      ? "transparent"
      : GRAY_LIGHTEST};
    border-color: ${TERTIARY_THEMES.includes(theme)
      ? "transparent"
      : GRAY_LIGHT};
    color: ${GRAY};
    cursor: initial;
    pointer-events: none;
  `;

const withOutlineStyles = ({ theme }) => {
  if (DARK_THEMES.includes(theme)) {
    return css`
      &:focus {
        outline: 1px solid ${WHITE};
        outline-offset: 2px;
      }
    `;
  }

  return css`
    &:focus {
      outline: 1px solid ${TEXT_PRIMARY};
      outline-offset: 2px;
    }
  `;
};

const defaultLoadingAnimation = keyframes`
  0% {
    transform: scaleX(0);
  }
  49% {
    transform-origin: left;
  }
  50% {
    transform-origin: right;
    transform: scaleX(1);
  }
  99% {
    transform-origin: right;
  }
  100% {
    transform-origin: left;
    transform: scaleX(0);
  }
`;

const defaultLoadingStyles = css`
  position: relative;
  z-index: 1;

  &::after {
    animation: ${defaultLoadingAnimation} 2s cubic-bezier(0.25, 1, 0.5, 1)
      infinite;
    background-color: ${GRAY_LIGHT};
    content: "";
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    transform-origin: left;
    width: 100%;
    z-index: -1;
  }
`;

const tertiaryLoadingAnimation = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const tertiaryLoadingStyles = css`
  svg:not(#${LOADING_ICON_ID}),
  img {
    display: none;
    visibility: hidden;
  }
`;

const withLoadingAnimationStyles = ({ isLoading, theme }) => {
  if (!isLoading) {
    return false;
  }

  return TERTIARY_THEMES.includes(theme)
    ? tertiaryLoadingStyles
    : defaultLoadingStyles;
};

export const TertiaryLoadingIcon = styled(LoadingIconBase).attrs({
  id: LOADING_ICON_ID
})`
  animation: ${tertiaryLoadingAnimation} 1s linear infinite;
  display: inline;
  margin-right: 8px;
  vertical-align: -2px;
`;

export const ButtonActionBase = styled.button.attrs(
  props =>
    props.isDisabled && props.as === actionElements.button && { disabled: true }
)`
  -webkit-font-smoothing: antialiased;
  align-items: center;
  border: 1px solid;
  border-radius: 0;
  box-sizing: border-box;
  display: inline-flex;
  font-family: "Source Sans Pro", Helvetica, Arial, Geneva, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  justify-content: center;
  padding: 0 24px;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  transition-duration: 0.3s;
  transition-property: background-color, border-color, color;
  transition-timing-function: cubic-bezier(0.25, 1, 0.5, 1);

  ${withThemeStyles};
  ${withSizeStyles};
  ${withOutlineStyles};
  ${withDisabledStyles};
  ${withLoadingAnimationStyles};

  &:hover {
    cursor: pointer;
  }

  * {
    box-sizing: border-box;
  }
`;
